if (typeof VideoPopup !== 'function') {
  class VideoPopup extends HTMLElement {
    constructor() {
      super();
      
      // State management
      this.state = {
        isPlaying: false,
        currentTime: 0,
        hasBeenPlayed: false,
        isInView: false,
        volume: 100,
        isMuted: true
      };

      this.player = null;
      this.playerReady = false;
      this.pendingPlay = false;
      this.isVimeo = this.getAttribute('data-product-media-type') === 'vimeo';
      this.intersectionDebounce = null;
      this.setupEventListeners();
      // Don't load YouTube API until needed - lazy load for performance
    }

    setupEventListeners() {
      // Play button click handler
      this.querySelector('[data-js-video-popup-link]').addEventListener('click', 
        this.handlePlayClick.bind(this)
      );

      // Intersection Observer for scroll detection
      // Reduced rootMargin (300px) for better performance - only load when close
      const options = {
        root: null,
        rootMargin: '300px',
        threshold: 0.1
      };

      this.observer = new IntersectionObserver((entries) => {
        // Debounce intersection callbacks to reduce performance impact
        clearTimeout(this.intersectionDebounce);
        this.intersectionDebounce = setTimeout(() => {
          entries.forEach(entry => {
            const wasInView = this.state.isInView;
            this.state.isInView = entry.isIntersecting;
            
            // Calculate distance from viewport
            const boundingRect = entry.boundingClientRect;
            const viewportHeight = window.innerHeight;
            let distanceFromViewport = 0;
            
            if (!entry.isIntersecting) {
              if (boundingRect.top > viewportHeight) {
                distanceFromViewport = boundingRect.top - viewportHeight;
              } else if (boundingRect.bottom < 0) {
                distanceFromViewport = Math.abs(boundingRect.bottom);
              }
            }
            
            // Only create container when within 300px (much closer for performance)
            if (!this.state.hasBeenPlayed && distanceFromViewport < 300 && this.shouldAutoplay()) {
              this.createVideoContainer();
            }
            
            // Start playing when actually in viewport or very close (100px)
            if ((entry.isIntersecting || distanceFromViewport < 100) && this.shouldAutoplay()) {
              if (!this.state.hasBeenPlayed) {
                this.createVideoContainer();
              }
              
              // For Vimeo, iframe autoplays when created
              if (this.isVimeo) {
                if (!this.state.isPlaying) {
                  this.state.isPlaying = true;
                  this.classList.add('video-opened');
                }
              } else {
                // For YouTube, lazy load API only when needed
                if (!window.YT && !this.youtubeAPILoading) {
                  this.loadYouTubeAPI();
                }
                if (this.playerReady && this.player && !this.state.isPlaying) {
                  this.startVideoPlayback();
                } else if (!this.playerReady) {
                  this.pendingPlay = true;
                }
              }
            } else if (this.state.isPlaying && wasInView && distanceFromViewport > 500) {
              // Pause when scrolled away
              if (this.isVimeo) {
                this.state.isPlaying = false;
                this.classList.remove('video-opened');
              } else if (this.player) {
                this.state.currentTime = this.player.getCurrentTime();
                this.state.isPlaying = false;
                this.player.pauseVideo();
                this.classList.remove('video-opened');
              }
            }
          });
        }, 100); // 100ms debounce
      }, options);

      this.observer.observe(this);

      // Handle page visibility changes
      document.addEventListener('visibilitychange', () => {
        if (document.hidden && this.state.isPlaying) {
          if (!this.isVimeo && this.player) {
            this.state.currentTime = this.player.getCurrentTime();
            this.player.pauseVideo();
          }
          this.state.isPlaying = false;
        }
      });
    }

    shouldAutoplay() {
      return window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
    }

    loadYouTubeAPI() {
      // Prevent multiple API loads
      if (this.youtubeAPILoading) return;
      this.youtubeAPILoading = true;
      
      if (!window.YT) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        tag.async = true;
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        
        // Handle multiple components waiting for API
        const originalCallback = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          if (originalCallback) originalCallback();
          if (this.state.hasBeenPlayed && !this.player) {
            this.initYouTubePlayer();
          }
        };
      } else {
        // API already loaded, init if container exists
        if (this.state.hasBeenPlayed && !this.player) {
          this.initYouTubePlayer();
        }
      }
    }

    handlePlayClick(event) {
      if (event) event.preventDefault();
      
      if (!this.state.hasBeenPlayed) {
        this.createVideoContainer();
      }

      if (this.player) {
        if (this.state.isPlaying) {
          this.player.pauseVideo();
        } else {
          this.player.playVideo();
          if (this.state.currentTime > 0) {
            this.player.seekTo(this.state.currentTime);
          }
        }
      }
    }

    createVideoContainer() {
      if (this.state.hasBeenPlayed) return; // Already created
      this.state.hasBeenPlayed = true; // Set synchronously — requestIdleCallback below can be deferred long enough for a second caller to slip past this guard otherwise.

      // Use requestIdleCallback for non-critical work if available
      const createContainer = () => {
        const container = this.querySelector('[data-js-video-popup-container]');
        const videoElement = this.querySelector('template').content.firstElementChild.cloneNode(true);
        container.appendChild(videoElement);

        // For Vimeo, iframe will autoplay automatically (autoplay=1 in URL)
        if (this.isVimeo) {
          this.playerReady = true;
          if (this.shouldAutoplay() && this.pendingPlay) {
            this.state.isPlaying = true;
            this.classList.add('video-opened');
            this.pendingPlay = false;
          }
        } else {
          // For YouTube, lazy load API only when container is created
          if (window.YT && window.YT.Player) {
            this.initYouTubePlayer();
          } else {
            this.loadYouTubeAPI();
          }
        }
      };
      
      // Use requestIdleCallback if available, otherwise immediate
      if (window.requestIdleCallback) {
        requestIdleCallback(createContainer, { timeout: 2000 });
      } else {
        createContainer();
      }
    }
    
    startVideoPlayback() {
      if (this.player && this.player.playVideo) {
        try {
          this.player.playVideo();
          this.state.isPlaying = true;
          this.classList.add('video-opened');
          this.pendingPlay = false;
        } catch (error) {
          console.log('Video play error:', error);
        }
      }
    }

    initYouTubePlayer() {
      const iframe = this.querySelector('.js-youtube');
      if (!iframe) return;

      this.player = new YT.Player(iframe, {
        events: {
          onReady: (event) => {
            // Set initial volume and mute state
            event.target.setVolume(this.state.volume);
            if (this.state.isMuted) event.target.mute();
            this.playerReady = true;
            
            // If we were waiting to play, start now
            if (this.pendingPlay && this.shouldAutoplay()) {
              this.startVideoPlayback();
            }
          },
          onStateChange: (event) => this.handlePlayerStateChange(event),
          onError: (error) => this.handleError(error)
        },
        playerVars: {
          autoplay: 0,
          controls: 1,
          rel: 0,
          showinfo: 0,
          modestbranding: 1,
          playsinline: 1,
          enablejsapi: 1,
          origin: window.location.origin,
          widget_referrer: window.location.origin,
          iv_load_policy: 3, // Disable annotations
          fs: 1 // Enable fullscreen
        }
      });
    }

    handlePlayerStateChange(event) {
      switch (event.data) {
        case YT.PlayerState.PLAYING:
          this.state.isPlaying = true;
          this.classList.add('video-opened');
          break;
          
        case YT.PlayerState.PAUSED:
          this.state.isPlaying = false;
          this.state.currentTime = this.player.getCurrentTime();
          break;
          
        case YT.PlayerState.ENDED:
          this.state.isPlaying = false;
          this.state.currentTime = 0;
          this.showEndScreen();
          break;
      }
    }

    showEndScreen() {
      const endScreen = document.createElement('div');
      endScreen.className = 'video-popup__end-screen';
      endScreen.innerHTML = `
        <div class="video-popup__end-content">
          <h3>Video Ended</h3>
          <button class="video-popup__restart">Replay Video</button>
          <button class="video-popup__close-end">Continue Browsing</button>
        </div>
      `;

      endScreen.querySelector('.video-popup__restart').addEventListener('click', () => {
        this.player.seekTo(0);
        this.player.playVideo();
        endScreen.remove();
      });

      endScreen.querySelector('.video-popup__close-end').addEventListener('click', () => {
        this.closeVideo();
      });

      this.querySelector('[data-js-video-popup-container]').appendChild(endScreen);
    }

    closeVideo() {
      if (!this.isVimeo && this.player) {
        this.state.currentTime = this.player.getCurrentTime();
        this.player.pauseVideo();
      }
      this.state.isPlaying = false;
      this.classList.remove('video-opened');
      this.querySelector('.video-popup__end-screen')?.remove();
    }

    handleError(error) {
      console.error('YouTube player error:', error);
      const errorMessage = document.createElement('div');
      errorMessage.className = 'video-popup__error';
      errorMessage.setAttribute('role', 'alert');
      errorMessage.textContent = 'An error occurred while playing the video. Please try again.';
      
      this.appendChild(errorMessage);
      setTimeout(() => {
        errorMessage.remove();
        this.closeVideo();
      }, 3000);
    }

    disconnectedCallback() {
      clearTimeout(this.intersectionDebounce);
      this.observer?.disconnect();
      if (this.player) {
        this.player.destroy();
        this.player = null;
      }
    }
  }

  if (typeof customElements.get('video-popup') === 'undefined') {
    customElements.define('video-popup', VideoPopup);
  }
}